import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from '../redux/store';
import { X } from 'lucide-react';

interface PopoutTabProps {
  tabType: string;
  title: string;
  windowId: string;
}

// Note: Popout tab functionality has been deprecated
// All previously supported tabs have been removed
const tabComponents: Record<string, React.ComponentType> = {};

const PopoutTab: React.FC<PopoutTabProps> = ({ tabType, title, windowId }) => {
  const TabComponent = tabComponents[tabType as keyof typeof tabComponents];

  useEffect(() => {
    // Notify parent that this window is ready
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'popout-ready',
        windowId,
        tabType
      }, window.location.origin);
    }
  }, [windowId, tabType]);

  const handleClose = () => {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'close-popout',
        windowId
      }, window.location.origin);
    }
    window.close();
  };

  if (!TabComponent) {
    return (
      <div className="flex items-center justify-center h-full bg-engramiq-dark-blue">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-engramiq-light-grey mb-2">Tab Not Available</h2>
          <p className="text-engramiq-bright-grey">Tab type "{tabType}" is not available for popout.</p>
          <button
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-engramiq-green text-engramiq-dark-blue rounded hover:bg-engramiq-green/80 transition-colors"
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-engramiq-dark-blue font-figtree flex flex-col">
      {/* Header */}
      <div className="bg-gradient-green-blue p-3 flex items-center justify-between">
        <h1 className="text-lg font-bold text-engramiq-dark-blue">{title}</h1>
        <button
          onClick={handleClose}
          className="p-1 hover:bg-engramiq-dark-blue/20 rounded text-engramiq-dark-blue"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        <Provider store={store}>
          <TabComponent />
        </Provider>
      </div>
    </div>
  );
};

// Function to render PopoutTab in a new window
export const renderPopoutTab = (tabType: string, title: string, windowId: string, targetWindow: Window) => {
  // Wait for the window to be ready
  const checkReady = () => {
    const container = targetWindow.document.getElementById('root');
    if (container) {
      const root = createRoot(container);
      root.render(
        <PopoutTab
          tabType={tabType}
          title={title}
          windowId={windowId}
        />
      );
    } else {
      setTimeout(checkReady, 100);
    }
  };

  // Listen for the window-ready message from the pop-out window
  const messageHandler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data.type === 'popout-message' &&
        event.data.windowId === windowId &&
        event.data.type === 'window-ready') {
      window.removeEventListener('message', messageHandler);
      setTimeout(checkReady, 200); // Small delay to ensure DOM is ready
    }
  };

  window.addEventListener('message', messageHandler);

  // Fallback: also check when the window loads
  setTimeout(checkReady, 300);
};

export default PopoutTab;
